// The raw typed list `getComponentsList` filters below. Same
// project-components + built-in-smbls-components union as
// prefill-values.js's `prefillComponentValue` (Rule 63 reuse) — every
// insertable component for the canvas properties/children dropdown,
// shaped like the sibling category lists here (`{ key, value }`, `value`
// a string so `getCategoriesList`'s `item.value.toLowerCase()` filter
// works the same way it does for domqlList/eventsList/etc.).
export async function getComponentList(type) {
  if (type !== 'components') {
    return []
  }

  const projectComponents = Object.keys(this.getRootState('components') || {})
  const smblsComponents = Object.keys(this.getRootContext('components') || {})
    .filter((key) => key.startsWith('smbls.'))
    .map((key) => key.replace('smbls.', ''))

  const keys = [...new Set([...projectComponents, ...smblsComponents])].sort()

  return keys.map((key) => ({ key, value: key }))
}

export async function getComponentsList() {
  const list = await this.call('getComponentList', 'components')
  const compList = list.filter(
    ({ isDivider, isSharedLib, isLabel }) => !isDivider && !isSharedLib && !isLabel
  )
  return { title: 'Components', items: compList }
}

export async function getDomqlList() {
  // FA204 / frank trap — moved from module scope into this function (sole
  // consumer, pure literal — see longPress.js for the identical shape).
  const DOMQL_ELEMENTS = [
    'extends',
    'childExtends',
    'children',
    'childProps',
    'childrenAs',
    'state',
    'tag',
    'key',
    'childExtendsRecursive',
    'query',
    'data',
    'scope'
  ]
  let domElements = DOMQL_ELEMENTS.map((item) => ({ key: item, value: item }))
  return { title: 'Domql', items: domElements.sort() }
}

export async function getEventsList() {
  // FA204 / frank trap — was `export const DOM_EVENTS` at module scope, but
  // the export was never load-bearing anywhere in this checkout (grep
  // confirms this function was the ONLY reader) — a sibling-export bare
  // read is exactly as unresolvable post-frank as a module-local read
  // (navbarTabMenu.js precedent) — moved inline as a self-contained local.
  const DOM_EVENTS = [
    'onAnimationcancel',
    'onAnimationend',
    'onAnimationiteration',
    'onAnimationstart',
    'onAuxclick',
    'onBeforeinput',
    'onBeforematchExperimental',
    'onBeforexrselectExperimental',
    'onBlur',
    'onClick',
    'onCompositionend',
    'onCompositionstart',
    'onCompositionupdate',
    'onContentvisibilityautostatechange',
    'onContextmenu',
    'onCopy',
    'onCut',
    'onDblclick',
    'onDOMActivateDeprecated',
    'onFocus',
    'onFocusin',
    'onFocusout',
    'onFullscreenchange',
    'onFullscreenerror',
    'onGotpointercapture',
    'onInput',
    'onKeydown',
    'onKeypressDeprecated',
    'onKeyup',
    'onLostpointercapture',
    'onMousedown',
    'onMouseenter',
    'onMouseleave',
    'onMousemove',
    'onMouseout',
    'onMouseover',
    'onMouseup',
    'onPaste',
    'onPointercancel',
    'onPointerdown',
    'onPointerenter',
    'onPointerleave',
    'onPointermove',
    'onPointerout',
    'onPointerover',
    'onPointerrawupdateExperimental',
    'onPointerup',
    'onScroll',
    'onScrollend',
    'onScrollsnapchangeExperimental',
    'onScrollsnapchangingExperimental',
    'onSecuritypolicyviolation',
    'onTouchcancel',
    'onTouchend',
    'onTouchmove',
    'onTouchstart',
    'onTransitioncancel',
    'onTransitionend',
    'onTransitionrun',
    'onTransitionstart',
    'onWheel'
  ]
  let domEventsList = DOM_EVENTS.map((item) => ({ key: item, value: item }))
  return { title: 'Events', items: domEventsList.sort() }
}

// Dynamic-imported per FA206 (npm-import-in-handler): a static top-of-file
// import of an external package used inside an exported handler relies on
// frank's runtime async-import workaround, and here that workaround didn't
// hold — `DEFAULT_CSS_PROPERTIES_LIST`'s bundler-renamed import alias
// leaked into the emitted function text verbatim (`frank to-json`:
// `unresolvable-identifier`, identifier literally named `o`, the minified
// alias esbuild assigned it — not a project typo). Both functions were
// already `async`, so the explicit dynamic import is a drop-in, zero-
// behavior-change fix.
//
// ⚠ DOCUMENTED FRANK EXCEPTION (2026-08-14) — the dynamic form does NOT
// actually survive serialization either, so neither shape works and this is
// not fixable in this package. `css-in-props` / `attrs-in-props` are plain
// npm packages, absent from frank's `EXTERNAL_PACKAGES` allowlist
// (smbls/plugins/frank/toJSON.js), so esbuild inlines them and rewrites the
// dynamic import into bundle-internal lazy-init calls:
//   getCssPropsList  → `await Promise.resolve().then(() => (init_esm(), esm_exports))`
//   getHTMLAttrsList → `await Promise.resolve().then(() => (init_esm2(), __scope.esm_exports2))`
// `init_esm`/`esm_exports` do not exist in the emitted function's eval scope.
// Re-measured both alternatives on 2026-08-14: the static named-import form
// puts the package total UP (64 → 65, `unresolvable-identifier`/`o` — the
// failure the comment above already records), the dynamic form leaves it
// where it is. Only `getHTMLAttrsList` is ever REPORTED (as
// `scope-read-name-collision`/`esm_exports2`, because `esm_exports2` happens
// to be a globalScope key while the un-suffixed `esm_exports` is not) —
// `getCssPropsList` is equally broken and the gate is silent about it, so a
// clean count here is not evidence. The real fix is framework-side
// (allowlist both packages so the dynamic import survives to the runtime
// importmap — both are public on npm, which is what that importmap requires);
// filed on tickets/fable.md. Leave both call sites as they are.
export async function getCssPropsList() {
  const { DEFAULT_CSS_PROPERTIES_LIST } = await import('css-in-props')
  // `DEFAULT_CSS_PROPERTIES_LIST` is a `Set` (css-in-props
  // src/props/defaults.js:1 — `export const DEFAULT_CSS_PROPERTIES_LIST = new
  // Set([…])`, 321 entries), and `Set.prototype.map` does not exist. Calling
  // `.map` on it directly threw `DEFAULT_CSS_PROPERTIES_LIST.map is not a
  // function` — i.e. the canvas properties dropdown's whole "Css" category
  // failed, in the normal Parcel bundle, independent of frank. Spread first.
  let cssProperties = [...DEFAULT_CSS_PROPERTIES_LIST].map((item) => ({
    key: item,
    value: item
  }))
  return { title: 'Css', items: cssProperties.sort() }
}

export async function getHTMLAttrsList() {
  const { HTML_ATTRIBUTES } = await import('attrs-in-props')
  let htmlAttributes = HTML_ATTRIBUTES['default'].map((item) => ({
    key: item,
    value: item
  }))
  return { title: 'Html', items: htmlAttributes.sort() }
}

export async function getCategoriesList() {
  const state = this.state
  const filter = state.filterText?.toLowerCase() || ''

  const components = await this.call('getComponentsList')
  const domqlList = await this.call('getDomqlList')
  const eventsList = await this.call('getEventsList')
  const cssPropsList = await this.call('getCssPropsList')
  const htmlAttrsList = await this.call('getHTMLAttrsList')

  let categories

  if (state.parent.listType === 'OnlyProperties') {
    categories = [domqlList, eventsList, cssPropsList, htmlAttrsList]
  } else if (state.parent.listType === 'OnlyChildren') {
    categories = [components]
  } else {
    categories = [components, domqlList, eventsList, cssPropsList, htmlAttrsList]
  }

  // Apply filter to each category
  return categories
    .map((cat) => ({
      title: cat.title,
      items: cat.items.filter((item) => item.value.toLowerCase().includes(filter))
    }))
    .filter((cat) => cat.items.length > 0)
}

export function moveSelectionInList(dir) {
  const root = this.state.parent
  const title = this.state.title

  const items = this.state.expanded
    ? this.state.items
    : [...this.state.items.slice(0, 5), { value: null, isMore: true }]

  const total = items.length
  let index = items.findIndex((item) =>
    item.isMore ? root.isMoreSelected : item.value === root.selectedItem
  )

  if (dir === 'down') {
    index = index < total - 1 ? index + 1 : 0
  } else if (dir === 'up') {
    index = index > 0 ? index - 1 : total - 1
  }

  const newItem = items[index]

  root.update({
    selectedItem: newItem.value,
    selectedTitle: title,
    isMoreSelected: !!newItem.isMore
  })

  const moreEl = this.parent.lookdown('More')
  if (newItem.isMore) {
    moreEl?.set({ background: 'gray3.5', round: 'Z' })
    this.call('addPropKey', '')
  } else {
    moreEl?.set({ background: 'transparent' })
    this.call('addPropKey', newItem.value)
  }
}

export function addPropKey(key) {
  const panel = this.getFloatingPanelElement()
  const inputEl = panel.inputElement

  if (!inputEl?.node) {
    throw new Error(`Input element ${inputEl} not found`)
  }

  inputEl.node.value = key
}
